import React, { useMemo, useState } from 'react';
import { useMarketStore } from '../../store/marketStore';
import { useSettingsStore, normalizeToBaseSymbol, StrategySymbol } from '../../store/settingsStore';
import { WccHistoricalStatsModal } from '../WccHistoricalStatsModal';
import './SignalInfoOverlay.css';
import { normalizeToBase } from '../../utils/symbolUtils';

const SignalInfoOverlay: React.FC = React.memo(() => {
  const { signalMarkers, symbol } = useMarketStore();
  const [showStatsModal, setShowStatsModal] = useState(false);
  const currentBase = symbol ? normalizeToBase(symbol) : '';
  const baseSymbol = normalizeToBaseSymbol(symbol || '') as StrategySymbol | null;

  // Get per-symbol settings if available, otherwise fall back to global
  const getWholeCandleForSymbol = useSettingsStore(state => state.getWholeCandleForSymbol);
  const globalWccSettings = useSettingsStore(state => state.wholeCandle);
  const wccSettings = baseSymbol ? getWholeCandleForSymbol(baseSymbol) : globalWccSettings;

  const takeItThreshold = wccSettings.takeItThreshold ?? 80;
  const tpPoints = wccSettings.tpPoints ?? 5;
  const slPoints = wccSettings.slPoints ?? 25;

  // Calculate 24h stats from signal markers for current symbol only
  const stats = useMemo(() => {
    const now = Date.now(); // Current time in milliseconds
    const twentyFourHoursAgo = now - 24 * 60 * 60 * 1000; // 24h ago in milliseconds

    // Filter to last 24 hours AND current symbol only
    const recentMarkers = signalMarkers.filter(
      (m) => m.timestamp >= twentyFourHoursAgo &&
             (m.type === 'bullish' || m.type === 'bearish') &&
             normalizeToBase(m.symbol) === currentBase
    );

    const totalSignals = recentMarkers.length;
    const bullCount = recentMarkers.filter((m) => m.type === 'bullish').length;
    const bearCount = recentMarkers.filter((m) => m.type === 'bearish').length;

    // Non-skipped signals that have profitability data
    const tradedSignals = recentMarkers.filter((m) => !m.skipped && m.isProfitable !== undefined);
    const winCount = tradedSignals.filter((m) => m.isProfitable === true).length;
    const lossCount = tradedSignals.filter((m) => m.isProfitable === false).length;
    const winRate = tradedSignals.length > 0 ? (winCount / tradedSignals.length) * 100 : 0;

    const skipCount = recentMarkers.filter((m) => m.skipped).length;
    const unskippedCount = totalSignals - skipCount;

    // Estimated P&L using TP/SL from WCC settings
    // MNQ: $2 per point
    const DOLLAR_PER_POINT = 2;
    const estimatedPnLPoints = (winCount * tpPoints) - (lossCount * slPoints);
    const estimatedPnL = estimatedPnLPoints * DOLLAR_PER_POINT;

    // TAKE IT signals - confidence + confluence > threshold (non-skipped only)
    const takeItSignals = recentMarkers.filter((m) => {
      if (m.skipped) return false;
      const confidence = m.confidence ?? 0;
      const confluence = m.confluenceScore ?? 0;
      return (confidence + confluence) > takeItThreshold;
    });
    const takeItCount = takeItSignals.length;

    // TAKE IT signals with profitability data
    const takeItWithProfitData = takeItSignals.filter((m) => m.isProfitable !== undefined);
    const takeItWins = takeItWithProfitData.filter((m) => m.isProfitable === true).length;
    const takeItLosses = takeItWithProfitData.filter((m) => m.isProfitable === false).length;
    const takeItWinRate = takeItWithProfitData.length > 0 ? (takeItWins / takeItWithProfitData.length) * 100 : 0;

    // Estimated P&L for TAKE IT signals only
    const takeItEstPnLPoints = (takeItWins * tpPoints) - (takeItLosses * slPoints);
    const takeItEstPnL = takeItEstPnLPoints * DOLLAR_PER_POINT;

    // ML TAKE signals - non-skipped signals where ML recommends TAKE
    const mlTakeSignals = recentMarkers.filter((m) => {
      if (m.skipped) return false;
      return m.mlRecommendation === 'TAKE';
    });
    const mlTakeCount = mlTakeSignals.length;

    // ML TAKE signals with profitability data
    const mlTakeWithProfitData = mlTakeSignals.filter((m) => m.isProfitable !== undefined);
    const mlTakeWins = mlTakeWithProfitData.filter((m) => m.isProfitable === true).length;
    const mlTakeLosses = mlTakeWithProfitData.filter((m) => m.isProfitable === false).length;
    const mlTakeWinRate = mlTakeWithProfitData.length > 0 ? (mlTakeWins / mlTakeWithProfitData.length) * 100 : 0;

    // Estimated P&L for ML TAKE signals only
    const mlTakeEstPnLPoints = (mlTakeWins * tpPoints) - (mlTakeLosses * slPoints);
    const mlTakeEstPnL = mlTakeEstPnLPoints * DOLLAR_PER_POINT;

    // MAE Statistics - calculate average MAE% for signals with profitability data
    const signalsWithMae = tradedSignals.filter((m) => m.maePercent !== undefined);
    const avgMaePercent = signalsWithMae.length > 0
      ? signalsWithMae.reduce((sum, m) => sum + (m.maePercent ?? 0), 0) / signalsWithMae.length
      : 0;

    // TAKE IT MAE
    const takeItWithMae = takeItWithProfitData.filter((m) => m.maePercent !== undefined);
    const takeItAvgMaePercent = takeItWithMae.length > 0
      ? takeItWithMae.reduce((sum, m) => sum + (m.maePercent ?? 0), 0) / takeItWithMae.length
      : 0;

    // ML TAKE MAE
    const mlTakeWithMae = mlTakeWithProfitData.filter((m) => m.maePercent !== undefined);
    const mlTakeAvgMaePercent = mlTakeWithMae.length > 0
      ? mlTakeWithMae.reduce((sum, m) => sum + (m.maePercent ?? 0), 0) / mlTakeWithMae.length
      : 0;

    return {
      totalSignals,
      bullCount,
      bearCount,
      winCount,
      lossCount,
      winRate,
      skipCount,
      unskippedCount,
      estimatedPnL,
      // TAKE IT stats
      takeItCount,
      takeItWins,
      takeItLosses,
      takeItWinRate,
      takeItEstPnL,
      // ML TAKE stats
      mlTakeCount,
      mlTakeWins,
      mlTakeLosses,
      mlTakeWinRate,
      mlTakeEstPnL,
      // MAE stats
      avgMaePercent,
      takeItAvgMaePercent,
      mlTakeAvgMaePercent,
    };
  }, [signalMarkers, currentBase, takeItThreshold, tpPoints, slPoints]);

  return (
    <div className="signal-info-overlay">
      <div className="signal-info-content">
        <div className="signal-info-item clickable" onClick={() => setShowStatsModal(true)}>
          <span className="signal-info-label">24H</span>
          <span className="signal-info-expand">▼</span>
        </div>
        <div className="signal-info-item">
          <span className="signal-info-label">SIGNALS:</span>
          <span className="signal-info-value">{stats.totalSignals}</span>
        </div>
        <div className="signal-info-item">
          <span className="signal-info-label">BULL:</span>
          <span className="signal-info-value bull">{stats.bullCount}</span>
        </div>
        <div className="signal-info-item">
          <span className="signal-info-label">BEAR:</span>
          <span className="signal-info-value bear">{stats.bearCount}</span>
        </div>
        <div className="signal-info-item">
          <span className="signal-info-label">TRADED:</span>
          <span className="signal-info-value">{stats.unskippedCount}</span>
        </div>
        <div className="signal-info-item">
          <span className="signal-info-label">WIN:</span>
          <span className="signal-info-value win">{stats.winCount}</span>
        </div>
        <div className="signal-info-item">
          <span className="signal-info-label">LOSS:</span>
          <span className="signal-info-value loss">{stats.lossCount}</span>
        </div>
        <div className="signal-info-item">
          <span className="signal-info-label">WR:</span>
          <span className="signal-info-value winrate">{stats.winRate.toFixed(0)}%</span>
        </div>
        <div className="signal-info-item">
          <span className="signal-info-label">SKIP:</span>
          <span className="signal-info-value">{stats.skipCount}</span>
        </div>
        <div className="signal-info-item">
          <span className="signal-info-label">EST P&L:</span>
          <span className={`signal-info-value ${stats.estimatedPnL >= 0 ? 'win' : 'loss'}`}>
            {stats.estimatedPnL >= 0 ? '+' : ''}${stats.estimatedPnL}
          </span>
        </div>
        <div className="signal-info-item" title="Average MAE% - how close to stop loss before resolving (lower is better)">
          <span className="signal-info-label">MAE:</span>
          <span className={`signal-info-value ${stats.avgMaePercent < 50 ? 'win' : 'loss'}`}>
            {stats.avgMaePercent.toFixed(0)}%
          </span>
        </div>
        <div className="signal-info-item muted">
          <span className="signal-info-label">TP:</span>
          <span className="signal-info-value">{tpPoints}</span>
        </div>
        <div className="signal-info-item muted">
          <span className="signal-info-label">SL:</span>
          <span className="signal-info-value">{slPoints}</span>
        </div>
        <div className="signal-info-divider">|</div>
        <div className="signal-info-item take-it">
          <span className="signal-info-label">TAKE IT:</span>
          <span className="signal-info-value">{stats.takeItCount}</span>
        </div>
        <div className="signal-info-item">
          <span className="signal-info-label">TI W/L:</span>
          <span className="signal-info-value">
            <span className="win">{stats.takeItWins}</span>/<span className="loss">{stats.takeItLosses}</span>
          </span>
        </div>
        <div className="signal-info-item">
          <span className="signal-info-label">TI WR:</span>
          <span className={`signal-info-value ${stats.takeItWinRate >= 50 ? 'win' : 'loss'}`}>{stats.takeItWinRate.toFixed(0)}%</span>
        </div>
        <div className="signal-info-item">
          <span className="signal-info-label">TI P&L:</span>
          <span className={`signal-info-value ${stats.takeItEstPnL >= 0 ? 'win' : 'loss'}`}>
            {stats.takeItEstPnL >= 0 ? '+' : ''}${stats.takeItEstPnL}
          </span>
        </div>
        <div className="signal-info-item" title="Average MAE% for TAKE IT signals">
          <span className="signal-info-label">TI MAE:</span>
          <span className={`signal-info-value ${stats.takeItAvgMaePercent < 50 ? 'win' : 'loss'}`}>
            {stats.takeItAvgMaePercent.toFixed(0)}%
          </span>
        </div>
        <div className="signal-info-divider">|</div>
        <div className="signal-info-item ml-take">
          <span className="signal-info-label">ML:</span>
          <span className="signal-info-value">{stats.mlTakeCount}</span>
        </div>
        <div className="signal-info-item">
          <span className="signal-info-label">ML W/L:</span>
          <span className="signal-info-value">
            <span className="win">{stats.mlTakeWins}</span>/<span className="loss">{stats.mlTakeLosses}</span>
          </span>
        </div>
        <div className="signal-info-item">
          <span className="signal-info-label">ML WR:</span>
          <span className={`signal-info-value ${stats.mlTakeWinRate >= 50 ? 'win' : 'loss'}`}>{stats.mlTakeWinRate.toFixed(0)}%</span>
        </div>
        <div className="signal-info-item">
          <span className="signal-info-label">ML P&L:</span>
          <span className={`signal-info-value ${stats.mlTakeEstPnL >= 0 ? 'win' : 'loss'}`}>
            {stats.mlTakeEstPnL >= 0 ? '+' : ''}${stats.mlTakeEstPnL}
          </span>
        </div>
        <div className="signal-info-item" title="Average MAE% for ML TAKE signals">
          <span className="signal-info-label">ML MAE:</span>
          <span className={`signal-info-value ${stats.mlTakeAvgMaePercent < 50 ? 'win' : 'loss'}`}>
            {stats.mlTakeAvgMaePercent.toFixed(0)}%
          </span>
        </div>
      </div>
      <WccHistoricalStatsModal
        isOpen={showStatsModal}
        onClose={() => setShowStatsModal(false)}
      />
    </div>
  );
});

export default SignalInfoOverlay;
